/**
 * Numbered headings transformation for markdown
 *
 * Transforms headings with the ## 0. syntax into numbered headings.
 * Using 0. as a placeholder allows markdown editors to recognize and highlight the heading.
 * Example:
 *   ## 0. First section     ->  ## 1. First section
 *   ## 0. Second section    ->  ## 2. Second section
 *   ### 0. Sub-section      ->  ### 2.1. Sub-section
 */

/**
 * Regex to match numbered heading markers
 * Captures:
 *   Group 1: The # characters (2-6)
 *   Group 2: The heading text (after the 0.)
 */
const NUMBERED_HEADING_REGEX = /^(#{2,6}) 0\. (.+)$/;

/**
 * Transform numbered heading markers (## 0. etc.) into actual numbered headings
 *
 * The numbering is hierarchical:
 * - ## 0. headings get 1., 2., 3.
 * - ### 0. headings get 1.1., 1.2., 2.1. (nested under parent ##)
 * - #### 0. headings get 1.1.1., 1.1.2. (nested under parent ###)
 *
 * When a higher-level heading is encountered, lower-level counters reset.
 *
 * @param content - The markdown content to transform
 * @returns Content with ## 0. syntax replaced by numbered headings
 */
export function processNumberedHeadings(content: string): string {
  // Counters for each heading level (index 0 = level 2, index 1 = level 3, etc.)
  // We support levels 2-6 (5 levels total)
  const counters = [0, 0, 0, 0, 0];

  const lines = content.split('\n');
  const outputLines: string[] = [];

  for (const line of lines) {
    const match = line.match(NUMBERED_HEADING_REGEX);

    if (match) {
      const [, hashes, text] = match;
      const level = hashes.length; // 2-6
      const counterIndex = level - 2; // 0-4

      // Increment this level's counter
      counters[counterIndex]++;

      // Reset all deeper level counters
      for (let i = counterIndex + 1; i < counters.length; i++) {
        counters[i] = 0;
      }

      // Build the number string (e.g., "1.2.3.")
      const numberParts: number[] = [];
      for (let i = 0; i <= counterIndex; i++) {
        // Only include if the counter is non-zero (has been used)
        // For the current level, always include
        if (i === counterIndex || counters[i] > 0) {
          numberParts.push(counters[i] || 1);
        }
      }

      const numberStr = numberParts.join('.') + '.';

      // Output the transformed heading
      outputLines.push(`${hashes} ${numberStr} ${text}`);
    } else {
      outputLines.push(line);
    }
  }

  return outputLines.join('\n');
}
